<script setup>
import { useUserThreads } from "@/composables/useUserThreads";
import { useNavigation } from "@/composables/useNavigation";
import InteractionCommunicationCard from "./InteractionCommunicationCard.vue";
import CustomButton from "./ui-kit/controls/CustomButton.vue";
import { useThreadsContextStore } from "@/stores/threads";
import { useInteractionTrainingStore } from "@/stores/interactionTraining";
import ThreadDetailsListBanner from "./ThreadDetailsListBanner.vue";


const interactionCommCardRef = ref({});
const threadsStore = useThreadsContextStore();
const thread = inject("THREAD");
const readOnlyMode = inject("READ_ONLY_MODE")
const { threadIds, getNextThread, getPreviousthread } = useUserThreads(thread);
const { navigateTo } = useNavigation();

const navigateToNewThread = (threadId) => {
  if (threadId) navigateTo(`/inbox/${threadId}`);
};

const interactionTrainingStore = useInteractionTrainingStore();

onMounted(async () => {
  if (
    interactionTrainingStore.isTrainingPopupOpen &&
    interactionTrainingStore.activeInteractionId in interactionCommCardRef.value
  ) {
    const focusedRef = interactionCommCardRef.value[interactionTrainingStore.activeInteractionId];
    await nextTick();
    focusedRef.$el.scrollIntoView({
      behavior: "smooth",
    });
  }
});
</script>

<template>
  <div class="left-view">
    <list-loader :show="threadsStore.isLoading" />
    <div class="scroll-wrapper">
      <CustomButton
        v-if="!readOnlyMode"
        class="previous-thread-button"
        buttonType="blue-transparent"
        @click.stop="getPreviousthread"
      >
        <span>Load Previous</span>
      </CustomButton>
      <section class="threads-list-container">
        <div
          v-for="threadId in threadIds"
          class="thread-container"
          :class="{ active: threadId === thread._id }"
          :key="threadId"
          @click.stop="navigateToNewThread(threadId)"
        >
          <ThreadDetailsListBanner :threadId="threadId" />
          <InteractionCommunicationCard
            v-for="interaction in threadsStore.getFormattedInteractionsByThread(threadId)"
            :key="interaction.id"
            :interaction="interaction"
            :showTrainingButton="!readOnlyMode"
            :ref="(el) => (interactionCommCardRef[interaction.id] = el)"
          >
            <template #top-right-corner>
              <span></span>
            </template>
          </InteractionCommunicationCard>
        </div>
      </section>
      <CustomButton
        v-if="!readOnlyMode"
        class="next-thread-button"
        buttonType="blue-transparent"
        @click="getNextThread"
      >
        <span>Load Next</span>
      </CustomButton>
    </div>
  </div>
</template>

<style scoped lang="scss">
.left-view {
  position: relative;
  height: 100%;
}

.scroll-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  background: $neutral-0;
  overflow-y: auto;
  overflow-x: hidden;
  border-radius: 8px;
  border: 1px solid $neutral-150;
  height: 100%;
  min-height: 500px;
}

.threads-list-container {
  flex: 1;
}

.status-badge {
  margin: auto;
  max-width: 200px;
  padding: 0.5rem 1rem;
}

.previous-thread-button {
  align-self: center;
  margin: 1rem 0 2rem 0;
  z-index: 10;
}

.next-thread-button {
  align-self: center;
  margin: 2rem 0 1rem 0;
  z-index: 10;
}

.thread-container {

  &.active {
    background-color: var(--primary-0);

    :deep(.interaction-communication-card) {
      border: 1px dashed var(--primary-200);
      border-top: none;

      .message-right {
        .message-text {
          background-color: var(--primary-50);
        }
      }
    }
  }
}
</style>
